/**
 * Encapsulates the functionality associated with a bearer token normally
 * passed in a request `x-tabserver-token` header as a base64-encoded
 * JSON object.
 *
 * Note the type definition annotations which are helpful when using this
 * Javascript class in Typescript.
 *
 * Example token:
 *
 * {
 *  "iss": "http://foo.daemon/some/public.jwk",
 *  "aud": "https://bar.daemon",
 *  "sub": "https://foo.daemon",
 *  "src": "https://some.com",
 *  "scope": {
 *    "http://localhost:9000/helloWorld1.html": "read write",
 *    "party:control": "grant revoke"
 *  }
 *  "iat": 1698576567000,
 *  "exp": 1698576344000,
 *  "sig": "long base64 string"
 * }
 *
 * Generally:
 *  iss - the issuer URL references the public JWK used to verify the sig.
 *  aud - the party handling the request, expressed as an origin.
 *  sub - the counterparty making the request, expressed as an origin.
 *  src - the source HTML file making the request, origin + pathname + search (no fragment).
 *  scope - a map of requested source -> scope pairs.
 *          A scope is a space-separated list of capabilities.
 *  iat - issued at time in absolute millis.
 *  exp - expiry time, ditto.
 *  sig - the base64 signature, verified by the JWK pointed to by iss.
 *
 * Normally the sub field must match the host portion of the iss URL. But
 * the possiblity remains open for an intermediary trusted by the party to
 * hold the public keys of counterparties.
 */

/**
 * @typedef {Object.<string, string>} Scope
 */

/**
 * @typedef TokenPayload
 * @property {string} iss   // URL of public key of counterparty making request.
 * @property {string} aud   // Party origin handling the request.
 * @property {string} sub   // Counterparty origin making the request.
 * @property {string} src   // Source HTML document context of the request excluding fragment.
 * @property {Scope} scope  // Map of scope (space-separated capabilities) keyed by source URL.
 * @property {number} iat   // Issued at time.
 * @property {number} exp   // Expiry time.
 * @property {Principal[]=} pre // Optional set of preceding principals this token acts after.
 * @property {string} sig   // Signature if signed.
 */

/**
 * @typedef Principal a (host, source) pair identifying a party acting through an app.
 * @property {string} counterparty the principal host (the `sub` host of its own token).
 * @property {string} src the source URL context of the principal.
 */

/**
 * @typedef {Object} Signatory the object to be served on the iss URL.
 * @property {string} src which is 'party:control' if the shell, or source URL if a runner.
 * @property {JsonWebKey} jwk
 */

/**
 * @typedef {Object} SignatoryCacheEntry
 * @property {Signatory} signatory
 * @property {CryptoKey} publicKey
 * @property {number} expiresAt
 */

/**
 * @typedef TokenSet
 * @type {Object<string, Token>}
 */

/** Must match algorithm in Keypair.js */
const ALGORITHM = {
  name: 'RSASSA-PKCS1-v1_5',
  hash: 'SHA-256',
}

/** Matches an origin with protocol and no path. */
const MATCH_ORIGIN = /https?:\/\/[^\/]+$/

/** Matches issuer paths for app runner public keys. */
const MATCH_APP_ISSUER_PATH = /^\/issuer\/[a-f0-9]{32}$/

/**
 * Allow a 30s leeway to allow for clock difference when checking
 * token issued at time. We allow our clock to be up to 30s behind
 * the system that generated a token.
 */
const TOKEN_IAT_LEEWAY_MILLIS = 1000 * 30

export class Token {
  // Standard sources.
  static Source = {
    PARTY_CONTROL: 'party:control', // Party control subject, a pseudo-source with scope granted to counterparties.
  }

  // Standard counterparty pattern.
  static Counterparty = {
    PUBLIC: 'public', // Public counterparty, matching requests with no host specified in sub.
  }

  // Standard scope labels.
  static Capability = {
    OPEN: 'open', // Capability to open source files and start tab runners.
    CLOSE: 'close', // Capability to close source files and stop tab runners.
    GRANT: 'grant', // Capability to create relation records.
    REVOKE: 'revoke', // Capability to delete relation records.
    OFFER: 'offer', // Capability to create a party offer for a device to claim.
    RETRACT: 'retract', // Capability to delete a party offer before it is claimed.
    DELETE: 'delete', // Capability to delete devices.
    CATALOG: 'catalog', // Capability to get sources, devices, alerts etc.
    ALIAS: 'alias', // Capability to set the alias for a counterparty host.
    UNALIAS: 'unalias', // Capability to unset the alias for a counterparty host.
    GET_ITEM: 'getitem', // Capability to get a storage item.
    SET_ITEM: 'setitem', // Capability to set (and remove) a storage item.
    ALERT: 'alert', // Capability to post and delete alerts.
    LOG: 'log', // Capability to view logs.
    CHAT: 'chat', // Capability to use generative chat.
    READ_MEMORY: 'read_memory', // Capability to read memory.
    WRITE_MEMORY: 'write_memory', // Capability to write memory.
    READ: 'read', // Capability to read from drive.
    WRITE: 'write', // Capability to write to drive.
  }

  static DEFAULT_EXPIRY_MILLIS = 1000 * 60 * 60 * 24
  static TOKEN_HEADER = 'x-tabserver-token'
  static SCOPE_SEPARATOR = /[\s,;\|]+/ // Specification per String.split() function.
  static #SIGNATORY_CACHE_TTL_MILLIS = 1000 * 60
  static #SIGNATORY_CACHE_MAX_ENTRIES = 2048

  /** @type{Map<string, SignatoryCacheEntry>} */
  static #signatoryCache = new Map()

  /** @type{Map<string, Promise<SignatoryCacheEntry>>} */
  static #signatoryCacheInFlight = new Map()

  /** @type{TokenPayload | null} */
  #payload = null

  /** @type{string | null} */
  #signatureBase64 = null

  /** @type{URL} */
  #audUrl

  /** @type{URL} */
  #subUrl

  /** @type{URL} */
  #srcUrl

  /** @type {Signatory | undefined} */
  #signatory // Set upon successful verification of signature.

  /**
   * Constructor takes either a signed base64 token, or a payload object.
   *
   * If successful, the payload and optionally the signature fields are
   * populated.
   *
   * @param {object | string} source a payload, or a base64 token
   */
  constructor(source) {
    let payload = null

    if (typeof source == 'object') {
      payload = source
    } else if (typeof source == 'string') {
      try {
        payload = JSON.parse(atob(source))
      } catch (_e) {
        throw 'Invalid token format'
      }
    } else {
      throw `Cannot construct token from ${typeof source}`
    }

    // Separate out the signature from the payload.
    this.#signatureBase64 = payload.sig
    delete payload.sig
    this.#payload = payload

    const {
      iss,
      aud,
      sub,
      src,
      scope,
      iat,
      exp,
      pre,
    } = payload

    // Check payload is complete.
    if ((!iss || !aud || !sub || !src || !scope || !iat || !exp)) {
      throw 'Token must include iss, aud, sub, src, scope, iat and exp'
    }

    // `pre` is optional (old tokens omit it). When present it must be an array
    // of {counterparty, src} principals. It is not reordered here — the signer
    // emits canonical entries and the JSON round-trip preserves the signed
    // bytes, exactly as for `scope`.
    if (pre !== undefined) {
      if (!Array.isArray(pre)) {
        throw 'The pre attribute must be an array of principals'
      }
      for (const principal of pre) {
        if (
          !principal || typeof principal.counterparty !== 'string' ||
          typeof principal.src !== 'string'
        ) {
          throw 'Each pre principal must have a counterparty and src'
        }
      }
    }

    // Check aud, sub and src fields are origins (i.e. proto://host.name)
    if (
      !aud.match(MATCH_ORIGIN) ||
      !sub.match(MATCH_ORIGIN)
    ) {
      throw 'The aud and sub attributes must be origins'
    }

    this.#audUrl = new URL(aud)
    this.#subUrl = new URL(sub)
    this.#srcUrl = new URL(src)

    // The `src` attribute must not have a fragment (reserved for browser-side).
    if (this.#srcUrl.hash) {
      throw 'The src attribute must have no fragment component.'
    }

    this.#payload = payload
  }

  /**
   * Generates the signature for the object using the private key provided by the
   * supplied callback and stores the result.
   *
   * @param {Promise<JsonWebKey>} privateJwkPromise the private key used for signing.
   * @return {Promise<string>} resolved with the base64 signature if signed successfully.
   */
  async signWith(privateJwkPromise) {
    if (this.#payload == null) {
      throw 'No payload to sign'
    }

    const privateJwk = await privateJwkPromise
    const privateKey = await crypto.subtle.importKey(
      'jwk',
      privateJwk,
      ALGORITHM,
      true,
      ['sign'],
    )
    const toSign = JSON.stringify(this.#payload)
    const messageBuffer = new TextEncoder().encode(toSign).buffer
    const bufferSource = new Uint8Array(messageBuffer)
    const signature = await crypto.subtle.sign(
      ALGORITHM,
      privateKey,
      bufferSource,
    )
    const signatureBase64 = Token.bytesToBase64(new Uint8Array(signature))
    this.#signatureBase64 = signatureBase64
    return signatureBase64
  }

  /**
   * Retrieves the signatory using the `iss` field, checks the signature
   * and if valid sets the signatory property in this instance.
   *
   * @throws {string} if signatory cannot be retrieved or signature doesn't match.
   */
  async verifySignatory() {
    const issuer = String(this.getIssuer())
    const {
      signatory,
      publicKey,
    } = await Token.#getCachedSignatory(issuer)
    this.#signatory = signatory
    this.#validateSignatory()
    await this.#checkSignatureWithPublicKey(publicKey)
  }

  /**
   * Fetches the signatory from the `iss` URL, setting the instance
   * property.
   *
   * The signatory comprises the `src` representing the app that has
   * signed the token and  the public `jwk`.
   *
   * @throws {string} error if fetch fails or returns an error response.
   */
  async fetchSignatory() {
    const issuer = String(this.getIssuer())
    this.#signatory = await Token.#fetchSignatoryForIssuer(issuer)
    this.#validateSignatory()
  }

  /**
   * Checks the signature using the public key provided by the supplied callback,
   * and stores the result.
   *
   * @param {Promise<JsonWebKey>} publicJwkPromise the public key used to verify the signature.
   * @throws {string} exception if signature cannot be verified or the public key is null or error.
   */
  async checkSignature(publicJwkPromise) {
    if (!this.#payload || !this.#signatureBase64) {
      throw 'checkSignature: no payload or signatureBase64'
    }

    const publicJwk = await publicJwkPromise
    const publicKey = await Token.#importPublicKey(
      publicJwk,
      String(this.getIssuer()),
    )
    await this.#checkSignatureWithPublicKey(publicKey)
  }

  /**
   * Checks the signature using an already imported public key.
   *
   * @param {CryptoKey} publicKey imported public key used to verify the signature.
   * @throws {string} exception if signature cannot be verified.
   */
  async #checkSignatureWithPublicKey(publicKey) {
    if (!this.#payload || !this.#signatureBase64) {
      throw 'checkSignature: no payload or signatureBase64'
    }
    const toCheck = JSON.stringify(this.#payload)
    const messageBuffer = new TextEncoder().encode(toCheck)
    const sigBuffer = Token.base64ToBytes(this.#signatureBase64)
    const isVerified = await crypto.subtle.verify(
      ALGORITHM,
      publicKey,
      sigBuffer,
      messageBuffer,
    )
    if (!isVerified) {
      throw `Signature rejected by issuer ${this.getIssuer()}`
    }
  }

  /**
   * Validates that the issuer and signatory source are permitted to sign
   * tokens asserting this token source.
   *
   * @throws {string} exception if the signatory source does not match the token source.
   */
  #validateSignatory() {
    if (!this.#signatory) {
      throw 'validateSignatory: no signatory'
    }

    const signatorySrc = this.#signatory.src
    const tokenSrc = this.getSrc()
    const issuer = this.getIssuer()

    if (issuer.origin !== this.getSub()) {
      throw `Token issuer origin '${issuer.origin}' should be token sub '${this.getSub()}'`
    }

    if (tokenSrc === Token.Source.PARTY_CONTROL) {
      if (signatorySrc !== Token.Source.PARTY_CONTROL) {
        throw `Token src '${tokenSrc}' cannot be signed by signatory src '${signatorySrc}'`
      }
    } else if (
      this.#srcUrl.protocol === 'http:' || this.#srcUrl.protocol === 'https:'
    ) {
      if (
        signatorySrc !== tokenSrc &&
        signatorySrc !== Token.Source.PARTY_CONTROL
      ) {
        throw `Token src '${tokenSrc}' cannot be signed by signatory src '${signatorySrc}'`
      }
    } else {
      throw `Unsupported token src '${tokenSrc}'`
    }

    if (signatorySrc === Token.Source.PARTY_CONTROL) {
      if (!issuer.pathname.startsWith('/device/')) {
        throw `Party control signatory issuer path '${issuer.pathname}' must start with /device/`
      }
      return
    }

    const signatoryUrl = new URL(signatorySrc)
    if (
      signatoryUrl.protocol !== 'http:' && signatoryUrl.protocol !== 'https:'
    ) {
      throw `Invalid signatory src '${signatorySrc}'`
    }
    if (!MATCH_APP_ISSUER_PATH.test(issuer.pathname)) {
      throw `App signatory issuer path '${issuer.pathname}' must match /issuer/<appId>`
    }
  }

  /**
   * Returns a signatory cache entry from cache or network.
   *
   * @param {string} issuer issuer URL.
   * @returns {Promise<SignatoryCacheEntry>}
   */
  static async #getCachedSignatory(issuer) {
    const cached = Token.#getFreshCachedSignatory(issuer)
    if (cached) {
      return cached
    }

    const inFlight = Token.#signatoryCacheInFlight.get(issuer)
    if (inFlight) {
      return await inFlight
    }

    const promise = (async () => {
      const signatory = await Token.#fetchSignatoryForIssuer(issuer)
      const publicKey = await Token.#importPublicKey(signatory.jwk, issuer)
      const entry = {
        signatory,
        publicKey,
        expiresAt: Date.now() + Token.#SIGNATORY_CACHE_TTL_MILLIS,
      }
      Token.#putCachedSignatory(issuer, entry)
      return entry
    })()

    Token.#signatoryCacheInFlight.set(issuer, promise)
    try {
      return await promise
    } finally {
      Token.#signatoryCacheInFlight.delete(issuer)
    }
  }

  /**
   * Returns a currently valid cached entry if present.
   *
   * @param {string} issuer issuer URL.
   * @returns {SignatoryCacheEntry | null}
   */
  static #getFreshCachedSignatory(issuer) {
    const entry = Token.#signatoryCache.get(issuer)
    if (!entry) {
      return null
    }

    if (entry.expiresAt <= Date.now()) {
      Token.#signatoryCache.delete(issuer)
      return null
    }

    // Refresh recency.
    Token.#signatoryCache.delete(issuer)
    Token.#signatoryCache.set(issuer, entry)
    return entry
  }

  /**
   * Adds/replaces a cache entry and enforces bounded size.
   *
   * @param {string} issuer issuer URL.
   * @param {SignatoryCacheEntry} entry signatory cache entry.
   */
  static #putCachedSignatory(issuer, entry) {
    if (Token.#signatoryCache.has(issuer)) {
      Token.#signatoryCache.delete(issuer)
    }
    Token.#signatoryCache.set(issuer, entry)

    while (Token.#signatoryCache.size > Token.#SIGNATORY_CACHE_MAX_ENTRIES) {
      const oldestKey = Token.#signatoryCache.keys().next().value
      if (!oldestKey) {
        break
      }
      console.log('Evicting token signatory cache entry due max size', {
        issuer,
        entryExpiresAt: entry.expiresAt,
        cacheMaxEntries: Token.#SIGNATORY_CACHE_MAX_ENTRIES,
      })
      Token.#signatoryCache.delete(oldestKey)
    }
  }

  /**
   * Fetches signatory details for the supplied issuer.
   *
   * @param {string} issuer issuer URL.
   * @returns {Promise<Signatory>}
   * @throws {string} if issuer cannot be read.
   */
  static async #fetchSignatoryForIssuer(issuer) {
    try {
      const response = await fetch(issuer, {
        headers: {
          'content-type': 'application/json',
        },
      })
      const json = await response.json()
      if ('error' in json) {
        throw json.error
      }
      const signatory = json.ok
      if (
        !signatory ||
        typeof signatory !== 'object' ||
        !signatory.jwk ||
        typeof signatory.jwk !== 'object'
      ) {
        throw 'Invalid signatory response'
      }
      if (
        typeof signatory.src !== 'string'
      ) {
        throw 'Invalid signatory src'
      }
      return signatory
    } catch (e) {
      throw `Failed to read signatory at ${issuer}: ${e}`
    }
  }

  /**
   * Imports and validates a JWK for signature verification.
   *
   * @param {JsonWebKey} publicJwk public key as JWK.
   * @param {string} issuer issuer URL.
   * @returns {Promise<CryptoKey>}
   * @throws {string} if key is missing or invalid.
   */
  static async #importPublicKey(publicJwk, issuer) {
    if (!publicJwk || typeof publicJwk !== 'object' || 'error' in publicJwk) {
      throw `Missing or invalid public JWK at issuer ${issuer}`
    }
    try {
      return await crypto.subtle.importKey('jwk', publicJwk, ALGORITHM, true, [
        'verify',
      ])
    } catch (_e) {
      throw `Missing or invalid public JWK at issuer ${issuer}`
    }
  }

  /**
   * Returns the payload with the `sig` property added.
   *
   * @return {TokenPayload} the payload with `sig` property.
   */
  getSignedPayload() {
    if (!this.#signatureBase64 || !this.#payload) {
      throw 'getSignedPayload: no signatureBase64 or payload'
    }

    return {
      ...this.#payload,
      sig: this.#signatureBase64,
    }
  }

  /**
   * Returns the payload whether signed or not.
   *
   * @return {TokenPayload} the payload object without signature.
   */
  getPayload() {
    if (!this.#payload) {
      throw 'getPayload: no payload'
    }
    return this.#payload
  }

  /**
   * Returns the `aud` field as a string.
   *
   * @return {string} the `aud` field value.
   */
  getAud() {
    return this.#audUrl.origin
  }

  /**
   * Returns the `aud` host which is the party name handling the request.
   *
   * @return {string} the party name.
   */
  getParty() {
    return this.#audUrl.host
  }

  /**
   * Returns the `sub` field as a string.
   *
   * @return {string} the `sub` field value.
   */
  getSub() {
    return this.#subUrl.origin
  }

  /**
   * Returns the `src` field as a string.
   *
   * @return {string} the `src` field value.
   */
  getSrc() {
    return String(this.#srcUrl)
  }

  /**
   * Returns the `sub` host, which is the counterparty name making the
   * request.
   *
   * @return {string} the counterparty name.
   */
  getCounterparty() {
    return this.#subUrl.host
  }

  /**
   * Returns the set of preceding principals this token acts after — the chain
   * of `{counterparty, src}` parties on whose behalf the primary principal
   * (`sub`/`src`) is acting. Empty for a first-hop token or a token minted
   * before `pre` existed.
   *
   * These are asserted by the primary signer and trusted transitively: because
   * permission is the intersection of the primary and every preceding principal,
   * a signer can only ever narrow access by adding entries, never exceed its own
   * grants. They are therefore not independently signatory-verified.
   *
   * @return {Principal[]} the preceding principals, or an empty array.
   */
  getPre() {
    return this.#payload?.pre ?? []
  }

  /**
   * Returns the source of the signatory who signed on behalf of the
   * counterparty. This is only available once the token is
   * verified, as it is provided by the `iss` endpoint.
   *
   * @returns {string} source of the verified signatory.
   */
  getSignatorySrc() {
    if (!this.#signatory) {
      throw 'getSignatorySrc: no signatory'
    }

    return this.#signatory.src
  }

  /**
   * Returns the `src` URL.
   *
   * @return {URL} the source URL.
   */
  getSourceUrl() {
    return this.#srcUrl
  }

  /**
   * Returns the `issuer` field as a URL. The counterparty is inferred from
   * the host name which generally matches the sub field.
   *
   * @return {URL} the issuer URL.
   */
  getIssuer() {
    return new URL(this.getPayload().iss)
  }

  /**
   * Returns the list of zero or more sources for which scope is requested.
   *
   * @return {string[]} a list of source URL strings.
   */
  getSources() {
    if (!this.#payload) {
      throw 'getSources: no payload'
    }

    const sources = []
    for (const source in this.#payload.scope) {
      sources.push(source)
    }
    return sources
  }

  /**
   * Returns the scope for the source as an array of capability tokens.
   *
   * These can be separated by any mix of space, comma, semicolon
   * or vertical bar.
   *
   * @param {string} source whose capabilities are returned.
   * @return {string[]} zero or more scope tokens.
   */
  getCapabilities(source) {
    if (!this.#payload) {
      throw 'getCapabilities: no payload'
    }

    const scope = this.#payload.scope
    if (!(source in scope)) {
      throw `Source '${source}' not in scope`
    }

    return scope[source].split(Token.SCOPE_SEPARATOR)
  }

  /**
   * Returns true if the supplied capability is present in the token
   * scope under the specified source, otherwise false.
   *
   * @param {string} source the source under which the capability is expected.
   * @param {string} capability the capability being tested.
   * @return {boolean} true if capability is present in the scope, otherwise false.
   */
  hasCapability(source, capability) {
    return this.getCapabilities(source).includes(capability)
  }

  /**
   * Checks the token is within the period defined by the `iat` and `exp`
   * date fields.
   *
   * @throws {string} exception if not within valid period.
   */
  checkPeriod() {
    if (!this.#payload) {
      throw 'checkPeriod: no payload'
    }

    const {
      iat,
      exp,
    } = this.#payload

    const now = Date.now()
    if (now < iat - TOKEN_IAT_LEEWAY_MILLIS) {
      throw 'Token is not yet valid'
    }

    if (now > exp) {
      throw 'Token has expired'
    }
  }

  /**
   * Returns the signed object including the `sig` property as a base64 string.
   *
   * @return {string} the signed object as a base64 string.
   */
  asSignedBase64() {
    return btoa(JSON.stringify(this.getSignedPayload()))
  }

  /**
   * Returns a base64 JSON string corresponding to the provided byte array.
   *
   * @param {Uint8Array} bytes to be encoded as a base64 string.
   * @returns {string} the encoded bytes.
   */
  static bytesToBase64(bytes) {
    const binString = Array.from(bytes, (x) => String.fromCodePoint(x)).join('')
    return btoa(binString)
  }

  /**
   * Returns the byte array decoded from the base64 string.
   *
   * @param {string} base64
   * @returns {Uint8Array} the decoded bytes.
   */
  static base64ToBytes(base64) {
    const binString = atob(base64)
    return Uint8Array.from(binString, (m) => m.codePointAt(0) ?? 0)
  }

  /**
   * Returns a new token using the x-tabserver-token header on the
   * request.
   *
   * @param {Request} request the request.
   * @return {Token | null} the token, or null if no header is present.
   * @throws {string} exception if token cannot be constructed.
   */
  static from(request) {
    const tokenBase64 = request.headers.get(Token.TOKEN_HEADER)
    if (tokenBase64) {
      return new Token(tokenBase64)
    }

    return null
  }

  /**
   * Converts an object whose string keys map to token objects
   * into  URL-friendly search params suitable for use in a
   * query string or fragment.
   *
   * The string keys are normally the audience hostname, or the
   * special key 'party' which holds the identity token whose
   * `aud` and `sub` are the same.
   *
   * @param {TokenSet} tokenSet the set of tokens keyed by string.
   * @returns {string}
   */
  static toSearchString(tokenSet) {
    const searchParams = new URLSearchParams()
    for (const key in tokenSet) {
      const token = tokenSet[key]
      const tokenBase64 = token.asSignedBase64()
      searchParams.append(key, tokenBase64)
    }
    return searchParams.toString()
  }
}
